
import { useMemo, useState, useEffect, useRef } from 'react';
import { Carousel, CarouselContent } from "@/components/ui/carousel";
import { DbProduct } from '@/hooks/useProduct';
import { ProductImageCarouselNavigation } from './ProductImageCarouselNavigation';
import { ProductColorIndicatorBadge } from './ProductColorIndicatorBadge';
import { ProductCategoryBadge } from './ProductCategoryBadge';
import { ProductImageSlide } from './ProductImageSlide';
import { useVariantImages } from '@/hooks/useVariantImages';

interface ProductImageGalleryProps {
  product: DbProduct;
  selectedColor?: any;
  selectedVariant?: any;
  aspectRatio?: 'square' | 'portrait' | 'landscape' | 'auto';
  objectFit?: 'cover' | 'contain';
  onCarouselNavigation?: () => void;
}

// Helper function to safely parse JSON data
const safeJsonParse = (data: any, defaultValue: any) => {
  if (!data) return defaultValue;
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return defaultValue;
  }
};

export const ProductImageGallery = ({ 
  product, 
  selectedColor,
  selectedVariant,
  aspectRatio = 'auto',
  objectFit = 'cover',
  onCarouselNavigation
}: ProductImageGalleryProps) => {
  const [imageTransitioning, setImageTransitioning] = useState(false);

  // Fetch variant-specific images if a variant is selected
  const { variantImages, imageUrls: variantImageUrls, hasVariantImages } = useVariantImages(
    product.id,
    selectedVariant?.size,
    selectedVariant?.pot_type,
    selectedVariant?.pot_color
  );

  // Find the featured category name if featured_category_id is set
  const featuredCategoryName = product.featured_category_id 
    ? product.categories.find(cat => cat.id === product.featured_category_id)?.name
    : product.category?.[0];

  // Calculate total number of unique images available
  const totalImageCount = useMemo(() => {
    const colorImages = safeJsonParse(product.color_images, {});
    const allColorImages = Object.values(colorImages).flat() as string[];
    const generalImages = product.image_urls || [];
    const allImages = [...new Set([...generalImages, ...allColorImages])];
    return allImages.length;
  }, [product.color_images, product.image_urls]);

  // Get all images from all colors when no color is selected, or specific color images when selected
  const imagesToShow = useMemo(() => {
    // Priority 1: Variant-specific images from variant_images table
    if (selectedVariant && hasVariantImages && variantImageUrls.length > 0) {
      return variantImageUrls.filter(url => url && typeof url === 'string');
    }

    // Priority 2: Direct variant image_url field (most common case)
    if (selectedVariant?.image_url && typeof selectedVariant.image_url === 'string') {
      return [selectedVariant.image_url];
    }

    // Priority 3: Legacy color-based images
    if (selectedColor?.id && product.color_images) {
      const colorImages = safeJsonParse(product.color_images, {});
      const colorSpecificImages = colorImages[selectedColor.id];
      
      if (colorSpecificImages && Array.isArray(colorSpecificImages) && colorSpecificImages.length > 0) {
        return colorSpecificImages.filter(url => url && typeof url === 'string');
      }
    }
    
    // Priority 4: All images (no color/variant selected)
    if (!selectedColor && !selectedVariant && product.color_images) {
      const colorImages = safeJsonParse(product.color_images, {});
      const allColorImages = Object.values(colorImages).flat() as string[];
      const generalImages = product.image_urls || [];
      const allImages = [...new Set([...generalImages, ...allColorImages])].filter(url => url && typeof url === 'string');
      if (allImages.length > 0) return allImages;
    }
    
    // Priority 5: General product images
    if (product.image_urls && Array.isArray(product.image_urls) && product.image_urls.length > 0) {
      return product.image_urls.filter(url => url && typeof url === 'string');
    }
    
    // Fallback: empty array (will show placeholder)
    return [];
  }, [selectedColor, selectedVariant, hasVariantImages, variantImageUrls, product.color_images, product.image_urls]);

  // Track previous images to detect actual changes (skip initial mount)
  const prevImagesRef = useRef<string[] | null>(null);
  
  // Effect to trigger transition only when images actually change (not on mount)
  useEffect(() => {
    const imagesKey = imagesToShow.join(',');
    const prevKey = prevImagesRef.current?.join(',');
    
    // Only trigger transition if images changed and it's not initial mount
    if (prevImagesRef.current !== null && imagesKey !== prevKey) {
      setImageTransitioning(true);
      const timer = setTimeout(() => setImageTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
    
    prevImagesRef.current = imagesToShow;
  }, [imagesToShow]);

  return (
    <div className="relative -mx-4 md:mx-0">
      <ProductColorIndicatorBadge selectedColor={selectedColor} />

      <Carousel 
        className="w-full md:rounded-lg overflow-hidden"
        opts={{
          loop: true,
          align: "start",
          dragFree: true,
          containScroll: "trimSnaps"
        }}
      >
        <CarouselContent>
          {imagesToShow && imagesToShow.length > 0 ? (
            imagesToShow.map((url, index) => (
              <ProductImageSlide 
                key={`${selectedColor?.id || 'all'}-${url}-${index}`}
                url={url}
                index={index}
                productName={product.name}
                selectedColor={selectedColor}
                aspectRatio={aspectRatio}
                objectFit={objectFit}
                imageTransitioning={imageTransitioning}
              />
            ))
          ) : (
            <ProductImageSlide 
              url="/placeholder.svg"
              index={0}
              productName={product.name}
              selectedColor={selectedColor}
              aspectRatio={aspectRatio}
              objectFit={objectFit}
              imageTransitioning={imageTransitioning}
            />
          )}
        </CarouselContent>
        
        {/* Enhanced navigation with dots and no color clearing */}
        <ProductImageCarouselNavigation 
          totalImageCount={imagesToShow?.length || 0}
        />
      </Carousel>
      
      {featuredCategoryName && (
        <ProductCategoryBadge categoryName={featuredCategoryName} />
      )}
    </div>
  );
};
